import { useAuth } from "@/context/AuthContext";
import { Sparkles, Lock } from "lucide-react";

/**
 * useFeature(key) — returns { enabled, plan }.
 * Reads from AuthContext where /auth/me populates `features` (set of enabled keys) + `plan`.
 */
export function useFeature(key) {
  const { features, plan, user } = useAuth();
  // super_admin always passes gates so platform ops keeps working across all tenants
  const enabled = user?.role === "super_admin" || (features || []).includes(key);
  return { enabled, plan };
}

/**
 * <Gated feature="regulators">
 *    <YourComponent />
 * </Gated>
 *
 * Soft gating (default): shows an "Upgrade required" pill instead of the child when disabled.
 * `hard`: renders nothing at all.
 */
export default function Gated({ feature, children, hard = false, fallback, className = "" }) {
  const { enabled, plan } = useFeature(feature);
  if (enabled) return children;
  if (hard) return null;
  if (fallback) return fallback;
  return (
    <div className={`border border-dashed border-primary/40 bg-red-50/40 rounded-md p-4 text-sm flex items-start gap-3 ${className}`} data-testid={`gated-${feature}`}>
      <Lock size={16} className="text-primary mt-0.5 shrink-0" />
      <div>
        <div className="font-semibold flex items-center gap-1.5">
          <Sparkles size={12} className="text-primary" /> Premium feature
        </div>
        <div className="text-xs text-muted-foreground mt-1">
          Your <b>{plan?.name || "current"}</b> plan does not include <span className="mono">{feature}</span>. Contact support or ask your platform admin to upgrade.
        </div>
      </div>
    </div>
  );
}

/** Small inline plan pill · usable in headers and sidebars */
export function PlanBadge({ compact = false }) {
  const { plan } = useAuth();
  if (!plan) return null;
  const tone = plan.tier >= 3
    ? "bg-primary text-primary-foreground"
    : plan.tier === 2
    ? "bg-amber-100 text-amber-800 border border-amber-300"
    : "bg-slate-100 text-slate-700 border border-slate-300";
  return (
    <span className={`inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-bold px-2 py-0.5 rounded ${tone}`}
      data-testid="plan-badge" title={plan.description}>
      <Sparkles size={9} />
      {compact ? plan.name.slice(0, 3) : plan.name}
    </span>
  );
}
